import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import api from "@/lib/api";
import { loadConfig, getConfig } from "@/lib/calcEngine";
import { formatCurrency, isoDate, formatDateDDMMYYYY } from "@/lib/format";
import Loader from "@/components/Loader";
import QrStatusCard from "@/components/QrStatusCard";
import ReferralLoginCard from "@/components/ReferralLoginCard";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { CalendarPlus, Cookie, Wheat, IndianRupee, CalendarCheck, ArrowRight, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const { plan } = usePlan();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [month, setMonth] = useState({ days: 0, cost: 0, rice: 0 });
  const [chart, setChart] = useState([]);

  useEffect(() => {
    if (sessionStorage.getItem("pm_goto_refer")) {
      sessionStorage.removeItem("pm_goto_refer");
      nav("/app/refer");
    } else if (sessionStorage.getItem("pm_after_login")) {
      const to = sessionStorage.getItem("pm_after_login");
      sessionStorage.removeItem("pm_after_login");
      nav(to);
    }
  }, [nav]);

  useEffect(() => {
    (async () => {
      await loadConfig();
      const cfg = getConfig();
      const today = new Date();
      const wd = today.getDay();
      const mp = cfg.menu_plans[wd] || cfg.menu_plans[String(wd)] || {};
      const laddoo = [2, 4, 6].includes(wd);
      setMenu({ display: mp.display, laddoo, day: cfg.day_names[wd] });

      const now = new Date();
      const start = isoDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const end = isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      const { data } = await api.get("/entries", { params: { start, end } });
      const entries = data.entries || [];
      let cost = 0, rice = 0;
      entries.forEach((e) => { cost += e.result.summary.grand_total; rice += e.result.summary.total_rice_kg; });
      setMonth({ days: entries.length, cost: Math.round(cost * 100) / 100, rice: Math.round(rice * 100) / 100 });

      // last 7 days
      const last7start = isoDate(new Date(Date.now() - 6 * 86400000));
      const { data: d2 } = await api.get("/entries", { params: { start: last7start, end: isoDate(today) } });
      const map = {};
      (d2.entries || []).forEach((e) => { map[e.date] = e.result.summary.grand_total; });
      const arr = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        arr.push({ label: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][d.getDay()], date: isoDate(d), cost: map[isoDate(d)] || 0 });
      }
      setChart(arr);
      setLoading(false);
    })();
  }, []);

  if (loading) return <Loader />;
  const maxCost = Math.max(1, ...chart.map((c) => c.cost));
  const showExpiring = plan && plan.enabled && plan.status === "active" && plan.days_left <= 15;
  const showExpired = plan && plan.enabled && plan.status === "expired";

  return (
    <div className="space-y-5">
      <ReferralLoginCard />
      <QrStatusCard />
      {showExpired && (
        <Link to="/app/plan" className="flex items-center justify-between gap-3 rounded-card bg-red-600 text-white p-4 shadow-warm" data-testid="dash-plan-expired">
          <span className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-5 h-5" /> Plan expired — Renew now</span>
          <ArrowRight className="w-5 h-5" />
        </Link>
      )}
      {showExpiring && (
        <Link to="/app/plan" className="flex items-center justify-between gap-3 rounded-card bg-thali-turmeric text-thali-ink p-4 shadow-warm" data-testid="dash-plan-expiring">
          <span className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-5 h-5" /> Your plan expires in {plan.days_left} day{plan.days_left === 1 ? "" : "s"} — Renew</span>
          <ArrowRight className="w-5 h-5" />
        </Link>
      )}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("welcome")}, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-thali-ink/60">{formatDateDDMMYYYY(new Date())} · {menu?.day}</p>
      </div>

      {/* Today's menu */}
      <div className="rounded-card bg-gradient-to-r from-thali-green to-thali-dark text-white p-5 shadow-warm" data-testid="dash-menu">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm opacity-80">{t("menu_today")}</div>
            <div className="text-xl font-heading font-bold mt-1">{menu?.display}</div>
          </div>
          {menu?.laddoo && <span className="bg-thali-turmeric text-thali-ink rounded-full px-3 py-1 text-sm font-bold flex items-center gap-1"><Cookie className="w-4 h-4" /> {t("laddoo_day")}</span>}
        </div>
        <Link to={`/app/entry?date=${isoDate(new Date())}`} className="btn-3d-saffron mt-4 w-full sm:w-auto" data-testid="dash-today-entry">
          <CalendarPlus className="w-5 h-5" /> {t("today_entry")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* This month */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-2">{t("this_month")}</h2>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: CalendarCheck, label: t("days_entered"), value: month.days, c: "#2563EB" },
            { icon: IndianRupee, label: t("total_cost"), value: formatCurrency(month.cost), c: "#0B6B3A" },
            { icon: Wheat, label: t("rice_kg"), value: `${month.rice} kg`, c: "#D97706" },
          ].map((s, i) => (
            <div key={i} className="card-thali p-3" data-testid={`dash-stat-${i}`}>
              <s.icon className="w-5 h-5 mb-1" style={{ color: s.c }} />
              <div className="text-xs text-thali-ink/60">{s.label}</div>
              <div className="font-num font-bold text-lg">{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Last 7 days chart */}
      <div className="card-thali">
        <h2 className="font-heading font-bold text-lg mb-3">{t("last7")}</h2>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chart}>
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
            <Tooltip cursor={{ fill: "#FFF8EC" }} formatter={(v) => formatCurrency(v)} labelFormatter={(l, p) => p?.[0]?.payload?.date ? formatDateDDMMYYYY(p[0].payload.date) : l} />
            <Bar dataKey="cost" radius={[6, 6, 0, 0]}>
              {chart.map((c, i) => <Cell key={i} fill={c.cost >= maxCost ? "#F97316" : "#0B6B3A"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
